import { 
  TextField, 
  FormControl, 
  FormLabel, 
  RadioGroup, 
  FormControlLabel, 
  Radio, 
  Checkbox, 
  FormGroup,
  Rating,
  Box,
  FormHelperText
} from '@mui/material';

/**
 * Renders the appropriate input component based on question type
 */
const QuestionRenderer = ({ question, value, onChange, error }) => {
  const { 
    id,
    questionText, 
    questionType, 
    options, 
    isRequired, 
    helpText,
    minValue,
    maxValue 
  } = question;

  const handleChange = (newValue) => {
    onChange(id, newValue);
  };

  const renderInput = () => {
    switch (questionType) {
      case 'number':
        return (
          <TextField
            type="number"
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            inputProps={{ 
              min: minValue || undefined, 
              max: maxValue || undefined 
            }}
            fullWidth
            size="small"
            error={!!error}
            helperText={error || helpText}
          />
        );

      case 'text':
        return (
          <TextField
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            fullWidth
            size="small"
            error={!!error}
            helperText={error || helpText}
          />
        );

      case 'textarea':
        return (
          <TextField
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            multiline
            rows={4}
            fullWidth
            error={!!error}
            helperText={error || helpText}
          />
        );

      case 'single_choice':
        const parsedOptions = typeof options === 'string' ? JSON.parse(options) : options;
        return (
          <FormControl component="fieldset" error={!!error}>
            <RadioGroup
              value={value || ''}
              onChange={(e) => handleChange(e.target.value)}
            >
              {(parsedOptions || []).map((option, index) => (
                <FormControlLabel
                  key={index}
                  value={option}
                  control={<Radio size="small" />}
                  label={option}
                />
              ))}
            </RadioGroup>
            {(error || helpText) && (
              <FormHelperText>{error || helpText}</FormHelperText>
            )}
          </FormControl>
        );

      case 'multiple_choice':
        const multiOptions = typeof options === 'string' ? JSON.parse(options) : options;
        const selectedValues = Array.isArray(value) ? value : [];
        
        return (
          <FormControl component="fieldset" error={!!error}>
            <FormGroup>
              {(multiOptions || []).map((option, index) => (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedValues.includes(option)}
                      onChange={(e) => {
                        const newValues = e.target.checked
                          ? [...selectedValues, option]
                          : selectedValues.filter(v => v !== option);
                        handleChange(newValues);
                      }}
                    />
                  }
                  label={option}
                />
              ))}
            </FormGroup>
            {(error || helpText) && (
              <FormHelperText>{error || helpText}</FormHelperText>
            )}
          </FormControl>
        );

      case 'rating':
        const maxRating = maxValue || 5;
        return (
          <Box>
            <Rating
              value={Number(value) || 0}
              onChange={(e, newValue) => handleChange(newValue)}
              max={maxRating}
              size="large"
            />
            {(error || helpText) && (
              <FormHelperText error={!!error}>{error || helpText}</FormHelperText>
            )}
          </Box>
        );

      case 'yes_no':
        return (
          <FormControl component="fieldset" error={!!error}>
            <RadioGroup
              row
              value={value || ''}
              onChange={(e) => handleChange(e.target.value)}
            >
              <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
              <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
            </RadioGroup>
            {(error || helpText) && (
              <FormHelperText>{error || helpText}</FormHelperText>
            )}
          </FormControl>
        );

      default:
        return (
          <TextField
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            fullWidth
            size="small"
            error={!!error}
            helperText={error || helpText}
          />
        );
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <FormLabel 
        required={isRequired}
        sx={{ 
          display: 'block', 
          mb: 1,
          fontWeight: 500,
          color: error ? 'error.main' : 'text.primary'
        }}
      >
        {questionText}
      </FormLabel>
      {renderInput()}
    </Box>
  );
};

export default QuestionRenderer;
